import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Chip, statusTone } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonKPIRow } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import {
  OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../components/ui/OrdinaFiltra';
import { fmtEur, fmtDate } from '../../lib/utils';
import { CandidateStage } from '@masamune/shared';

const PAGE_SIZE = 25;

/**
 * Ordina e filtra (24/9), una coppia per ogni lista. Corsi e candidati sono
 * paginati: ordina l'API (`sort`). Docenti, aziende partner e body rental
 * arrivano interi: si ordinano qui. Ogni lista ha i suoi parametri nell'URL
 * (`ordina`, `tipo`, `stato`, `fase`, `solo`), e cambiando lista si tolgono:
 * il "tipo" dei corsi non è il "tipo" dei docenti.
 */
const PARAMETRI_LISTA = ['ordina', 'tipo', 'stato', 'fase', 'solo'];

type Pillar = 'academy' | 'bodyrental';
type AcademySection = 'corsi' | 'pool' | 'docenti' | 'aziende';

interface TalentStats {
  coursesActive: number;
  coursesFormatemp: number;
  coursesFondimpresa: number;
  studentsInClass: number;
  readyPlacement: number;
  recentReady: number;
  talentRevenue?: number;
  talentMarginPct?: number;
  placementYTD: number;
}

export function TalentPage() {
  const [params, setParams] = useSearchParams();
  const pillar = (params.get('p') as Pillar) === 'bodyrental' ? 'bodyrental' : 'academy';
  const section = (params.get('s') as AcademySection) || 'corsi';
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const showMargin = !!user?.role.canSeeMargin;

  const togliParametriLista = (sp: URLSearchParams) => { for (const k of PARAMETRI_LISTA) sp.delete(k); };
  const setPillar = (p: Pillar) => setParams((prev) => {
    // Si resta sulla stessa lista solo tornando alla prima scheda dello stesso pilastro
    const ora = prev.get('p') === 'bodyrental' ? 'bodyrental' : 'academy';
    if (ora !== p || (prev.get('s') || 'corsi') !== 'corsi') togliParametriLista(prev);
    prev.set('p', p); prev.delete('s'); return prev;
  }, { replace: true });
  const setSection = (s: AcademySection) => setParams((prev) => {
    if ((prev.get('s') || 'corsi') !== s) togliParametriLista(prev);
    prev.set('p', 'academy'); prev.set('s', s); return prev;
  }, { replace: true });

  // Pilastri: il selettore vive SOTTO la stat-band del pilastro attivo,
  // quindi lo costruiamo qui e lo passiamo ai due pilastri.
  const pillarSwitch = (
    <div className="pillar-switch mb-6">
      <button className="pillar" aria-selected={pillar === 'academy'} onClick={() => setPillar('academy')}>
        <Icon name="talent" size={16} />
        <div className="text-left">
          <div className="pillar-t">Academy</div>
          <div className="pillar-s">Corsi · Talent pool · Recruiting</div>
        </div>
      </button>
      <button className="pillar" aria-selected={pillar === 'bodyrental'} onClick={() => setPillar('bodyrental')}>
        <Icon name="brief" size={16} />
        <div className="text-left">
          <div className="pillar-t">Body Rental</div>
          <div className="pillar-s">Somministrazione a margine</div>
        </div>
      </button>
    </div>
  );

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            numeri NUDI sul foglio (stat-band), zero scatole ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Talent</h1>
          <div className="ed-sub">
            {pillar === 'academy'
              ? 'Academy: corsi, talent pool e pipeline di recruiting dentro ad ogni corso.'
              : 'Body Rental: richieste delle aziende, professionisti ingaggiati e margine.'}
          </div>
        </div>
        <div className="flex gap-2 pb-1">
          {/* Una sola primaria per vista: la creazione vive nella toolbar della tabella attiva */}
          <DesktopOnly
            cosa="L'esportazione dei dati Talent"
            perche="serve a produrre un file di lavoro; sul telefono restano consultabili corsi, candidati e richieste."
          >
            <ActionsMenu
              variant="ghost"
              items={[
                { key: 'export', label: 'Esporta', icon: 'export', onClick: () => openModal('export', { what: 'Talent' }) },
              ]}
            />
          </DesktopOnly>
        </div>
      </div>

      {pillar === 'academy' ? (
        <AcademyPillar section={section} setSection={setSection} showMargin={showMargin} pillarSwitch={pillarSwitch} />
      ) : (
        <BodyRentalPillar showMargin={showMargin} pillarSwitch={pillarSwitch} />
      )}
    </div>
  );
}

// =====================================================================
// PILASTRO ACADEMY
// =====================================================================
function AcademyPillar({ section, setSection, showMargin, pillarSwitch }: {
  section: AcademySection; setSection: (s: AcademySection) => void; showMargin: boolean; pillarSwitch: ReactNode;
}) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['talent-stats'],
    queryFn: () => api.get<TalentStats>('/talent/stats'),
  });

  const subtabs: { k: AcademySection; l: string }[] = [
    { k: 'corsi', l: 'Corsi' },
    { k: 'pool', l: 'Candidati / Discenti' },
    { k: 'docenti', l: 'Albo docenti' },
    { k: 'aziende', l: 'Aziende partner' },
  ];

  return (
    <>
      {isLoading && !stats ? (
        <SkeletonKPIRow count={4} />
      ) : (
        /* Numeri nudi sul foglio, separati da hairline: niente scatole né icone */
        <div className="stat-band">
          <div>
            <div className="st-label">Corsi attivi</div>
            <div className="st-value"><AnimatedNumber value={stats?.coursesActive ?? 0} /></div>
            <div className="st-delta">{stats?.coursesFormatemp ?? 0} Formatemp · {stats?.coursesFondimpresa ?? 0} Fondimpresa</div>
          </div>
          <div>
            <div className="st-label">Discenti in aula</div>
            <div className="st-value"><AnimatedNumber value={stats?.studentsInClass ?? 0} /></div>
          </div>
          <div>
            <div className="st-label">Pronti al placement</div>
            <div className="st-value"><AnimatedNumber value={stats?.readyPlacement ?? 0} /></div>
            <div className="st-delta">+{stats?.recentReady ?? 0} questa settimana</div>
          </div>
          {showMargin ? (
            <div>
              <div className="st-label">Margine Talent YTD</div>
              <div className="st-value"><AnimatedNumber value={stats?.talentMarginPct ?? 0} format={(n) => n.toFixed(1)} /><span className="u">%</span></div>
              <div className="st-delta">{stats?.talentRevenue ? `su € ${(stats.talentRevenue / 1000).toFixed(0)}k ricavi` : '—'}</div>
            </div>
          ) : (
            <div>
              <div className="st-label">Margine Talent</div>
              <div className="st-value mute-2">•••</div>
              <div className="st-delta">Riservato</div>
            </div>
          )}
        </div>
      )}

      {pillarSwitch}

      <div className="page-tabs">
        {subtabs.map((t) => (
          <div key={t.k} className="page-tab" aria-selected={section === t.k} onClick={() => setSection(t.k)}>{t.l}</div>
        ))}
      </div>

      {section === 'corsi'   && <CoursesTable />}
      {section === 'pool'    && <CandidatesTable />}
      {section === 'docenti' && <TeachersTab />}
      {section === 'aziende' && <PartnerCompaniesTab />}
    </>
  );
}

// =====================================================================
// PILASTRO BODY RENTAL
// =====================================================================
interface BRRequest {
  id: string; title: string; status: string; neededRoles: number;
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; name: string; code: string } | null;
  engagementCount: number; assignedCount: number;
  revenue: number; cost?: number; margin?: number; createdAt: string;
}

const BR_OPEN = ['RICHIESTA', 'RICERCA', 'PROPOSTO', 'ATTIVO'];

// La lista arriva intera (fino a 100): si ordina qui. Il server la manda
// dalla più recente, ed è il predefinito.
const ORDINI_BR: OpzioneOrdina<BRRequest>[] = [
  { key: 'recenti', label: 'Richieste più recenti prima', criteri: [perData((r) => r.createdAt, 'decrescente')] },
  { key: 'ricavo', label: 'Prima le richieste che rendono di più', criteri: [perNumero((r) => r.revenue, 'decrescente')] },
  {
    key: 'da-coprire', label: 'Prima quelle con più figure da assegnare',
    criteri: [perNumero((r) => r.engagementCount - r.assignedCount, 'decrescente'), perData((r) => r.createdAt, 'decrescente')],
  },
  { key: 'cliente', label: 'Cliente dalla A alla Z', criteri: [perTesto((r) => r.client.shortName), perData((r) => r.createdAt, 'decrescente')] },
];

const FILTRI_BR: FiltroDef<BRRequest>[] = [
  {
    param: 'solo', label: 'Quali richieste',
    opzioni: [
      { value: 'aperte', label: 'Solo le richieste aperte' },
      { value: 'da-coprire', label: 'Solo quelle con figure ancora da assegnare' },
    ],
    test: (r, v) => (v === 'aperte' ? BR_OPEN.includes(r.status) : r.assignedCount < r.engagementCount),
  },
  {
    param: 'stato', label: 'Stato della richiesta',
    opzioni: [
      { value: 'RICHIESTA', label: 'Solo quelle appena chieste dal cliente' },
      { value: 'RICERCA', label: 'Solo quelle in cui cerchiamo le figure' },
      { value: 'PROPOSTO', label: 'Solo quelle proposte al cliente' },
      { value: 'ATTIVO', label: 'Solo gli ingaggi in corso' },
      { value: 'TERMINATO', label: 'Solo gli ingaggi conclusi' },
      { value: 'ANNULLATO', label: 'Solo quelle annullate' },
    ],
    test: (r, v) => r.status === v,
  },
];

function BodyRentalPillar({ showMargin, pillarSwitch }: { showMargin: boolean; pillarSwitch: ReactNode }) {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const { data, isLoading } = useQuery({
    queryKey: ['body-rental', 'list'],
    queryFn: () => api.get<{ items: BRRequest[] }>('/body-rental?pageSize=100'),
  });
  const items = data?.items ?? [];
  // I numeri in alto restano su tutte le richieste; la tabella segue i filtri
  const ordina = useOrdina(ORDINI_BR);
  const filtri = useFiltri(FILTRI_BR, data?.items);
  const righe = useMemo(() => ordina.applica(filtri.applica(data?.items ?? [])), [data, ordina, filtri]);

  const open = items.filter((r) => BR_OPEN.includes(r.status)).length;
  const figures = items.reduce((a, r) => a + r.engagementCount, 0);
  const assigned = items.reduce((a, r) => a + r.assignedCount, 0);
  const revenue = items.reduce((a, r) => a + (r.revenue || 0), 0);
  const margin = items.reduce((a, r) => a + (r.margin || 0), 0);
  const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;

  return (
    <>
      {isLoading && !data ? (
        <SkeletonKPIRow count={4} />
      ) : (
        /* Numeri nudi sul foglio, separati da hairline: niente scatole né icone */
        <div className="stat-band">
          <div>
            <div className="st-label">Richieste aperte</div>
            <div className="st-value"><AnimatedNumber value={open} /></div>
            <div className="st-delta">{items.length} totali</div>
          </div>
          <div>
            <div className="st-label">Figure ingaggiate</div>
            <div className="st-value"><AnimatedNumber value={assigned} /><span className="u">/{figures}</span></div>
            <div className="st-delta">assegnate</div>
          </div>
          <div>
            <div className="st-label">Ricavo body rental</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={revenue} format={fmtEur} /></div>
          </div>
          {showMargin ? (
            <div>
              <div className="st-label">Margine</div>
              <div className="st-value"><AnimatedNumber value={marginPct} format={(n) => n.toFixed(1)} /><span className="u">%</span></div>
              <div className="st-delta">€ {fmtEur(margin)}</div>
            </div>
          ) : (
            <div>
              <div className="st-label">Margine</div>
              <div className="st-value mute-2">•••</div>
              <div className="st-delta">Riservato</div>
            </div>
          )}
        </div>
      )}

      {pillarSwitch}

      {/* .filterbar--float e non .ed-toolbar: sono la stessa cosa — la riga
          sopra la lista — disegnata in due modi, e il canonico è questo
          (19 schermate contro 2). Referto §2.10. */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <span className="mute text-[13px]">Professionisti ingaggiati a margine per i clienti</span>
        <div className="spacer" style={{ flex: 1 }} />
        <span className="mono mute text-[12px]">{righe.length === items.length ? `${items.length} richieste` : `${righe.length} di ${items.length} richieste`}</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
        {/* Il gemello sotto il pollice è la barra in fondo (mobile.md §4). */}
        <button className="btn btn-sm btn-primary d-only" onClick={() => openModal('newBodyRental')}><Icon name="plus" size={12} /> Nuova richiesta</button>
      </div>
      <DesktopOnly
        cosa="La creazione di una nuova richiesta body rental"
        perche="richiede profili, condizioni e dati economici da compilare con calma."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newBodyRental')}><Icon name="plus" size={12} /> Nuova richiesta</button>
        </div>
      </DesktopOnly>

      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th>Richiesta</th><th>Cliente</th><th>Progetto</th><th className="num">Figure</th><th>Stato</th>
              <th className="num">Ricavo</th>{showMargin && <th className="num">Margine</th>}
            </tr>
          </thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/talent/body-rental/${r.id}`)}>
                {/* Il titolo della richiesta e il cliente stanno insieme in
                    cima: è quello che fa riconoscere la riga. Il progetto si
                    legge aprendo, come dice mobile.md §3. */}
                <td data-m="hd" className="font-semibold">{r.title}</td>
                <td data-m="key" data-l="Cliente">{r.client.shortName}</td>
                <td data-m="off" className="mute">{r.project?.name ?? '—'}</td>
                {/* x/y assegnate, piatto in mono: il dettaglio figure vive nella pagina richiesta */}
                <td data-m="key" data-l="Figure" className="num mono">{r.assignedCount}/{r.engagementCount}</td>
                <td data-m="key" data-l="Stato"><Chip tone={statusTone(r.status)}>{r.status}</Chip></td>
                <td data-m="key" data-l="Ricavo" className="num">€ {fmtEur(r.revenue)}</td>
                {showMargin && <td data-m="off" className="num tbl-metric accent">€ {fmtEur(r.margin ?? 0)}</td>}
              </tr>
            ))}
            {!isLoading && !righe.length && (
              <tr>
                <td colSpan={showMargin ? 7 : 6} style={{ padding: 0 }}>
                  {items.length ? (
                    <EmptyState
                      icon="brief"
                      title="Nessuna richiesta con questi filtri"
                      description="Togli un filtro per vedere le altre."
                      action={<button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
                    />
                  ) : (
                    <EmptyState
                      icon="brief"
                      title="Nessuna richiesta di somministrazione"
                      description="Crea la prima dal pulsante «Nuova richiesta»."
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================================
// AZIENDE PARTNER (Academy)
// =====================================================================
interface PartnerCompany {
  id: string; legalName: string; shortName: string; sector: string | null; city: string | null;
  activePlacements: number; totalPlacements: number;
  latestPlacement: { startDate: string | null; status: string; candidate: { firstName: string; lastName: string } } | null;
}

// Arrivano intere, in ordine alfabetico dal server: è il predefinito.
const ORDINI_AZIENDE: OpzioneOrdina<PartnerCompany>[] = [
  { key: 'nome', label: 'Azienda dalla A alla Z', criteri: [perTesto((c) => c.legalName)] },
  { key: 'attivi', label: 'Prima chi ha più placement in corso', criteri: [perNumero((c) => Number(c.activePlacements) || 0, 'decrescente'), perTesto((c) => c.legalName)] },
  { key: 'storici', label: 'Prima chi ha ospitato più placement', criteri: [perNumero((c) => Number(c.totalPlacements) || 0, 'decrescente'), perTesto((c) => c.legalName)] },
  { key: 'ultimo', label: 'Ultimo placement più recente prima', criteri: [perData((c) => c.latestPlacement?.startDate, 'decrescente')] },
];

const FILTRI_AZIENDE: FiltroDef<PartnerCompany>[] = [
  {
    param: 'solo', label: 'Placement',
    opzioni: [{ value: 'attivi', label: 'Solo le aziende con un placement in corso' }],
    test: (c) => (Number(c.activePlacements) || 0) > 0,
  },
];

function PartnerCompaniesTab() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['talent-partner-companies'],
    queryFn: () => api.get<PartnerCompany[]>('/talent/partner-companies'),
  });

  const items = data ?? [];
  const activePlacements = items.reduce((a, c) => a + (Number(c.activePlacements) || 0), 0);
  const totalPlacements = items.reduce((a, c) => a + (Number(c.totalPlacements) || 0), 0);
  const ordina = useOrdina(ORDINI_AZIENDE);
  const filtri = useFiltri(FILTRI_AZIENDE, data);
  const righe = useMemo(() => ordina.applica(filtri.applica(data ?? [])), [data, ordina, filtri]);

  return (
    <>
      {/* .filterbar--float, come le altre 19 schermate (referto §2.10). */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <span className="mute text-[13px]">Aziende che hanno ospitato discenti in placement</span>
        <div className="spacer" style={{ flex: 1 }} />
        <span className="mono mute text-[12px]">{items.length} aziende · {activePlacements} placement attivi · {totalPlacements} storici</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
      </div>
      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
          <thead><tr><th>Azienda</th><th>Settore</th><th>Città</th><th className="num">Placement attivi</th><th className="num">Storici</th><th>Ultimo placement</th></tr></thead>
          <tbody>
            {righe.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                <td data-m="hd" className="font-semibold">{c.legalName}</td>
                <td data-m="key" data-l="Settore">{c.sector ?? '—'}</td>
                <td data-m="key" data-l="Città" className="mute">{c.city ?? '—'}</td>
                <td data-m="key" data-l="Placement attivi" className={`num tbl-metric ${c.activePlacements > 0 ? 'accent' : ''}`}>{c.activePlacements}</td>
                <td data-m="key" data-l="Storici" className="num mono">{c.totalPlacements}</td>
                {/* Nome e data dell'ultimo placement sono un testo lungo:
                    si prende una riga sua invece di spezzarsi in colonna. */}
                <td data-m="row" data-l="Ultimo placement" className="text-[12px]">
                  {c.latestPlacement
                    ? <>{c.latestPlacement.candidate.firstName} {c.latestPlacement.candidate.lastName} <span className="mono mute ml-1">{fmtDate(c.latestPlacement.startDate)}</span></>
                    : <span className="mute-2">—</span>}
                </td>
              </tr>
            ))}
            {!isLoading && !righe.length && (
              <tr>
                <td colSpan={6} style={{ padding: 0 }}>
                  {items.length ? (
                    <EmptyState
                      icon="brief"
                      title="Nessuna azienda con questi filtri"
                      description="Togli il filtro per vedere le altre."
                      action={<button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
                    />
                  ) : (
                    <EmptyState
                      icon="brief"
                      title="Nessuna azienda partner"
                      description="Le aziende compaiono qui quando ospitano discenti in placement."
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================================
// ALBO DOCENTI (Academy)
// =====================================================================
interface Teacher {
  kind: 'user' | 'supplier'; id: string; name: string; initials: string; email: string | null;
  city: string | null; hourlyRate: number | null;
  coursesCount: number; activeCoursesCount: number; hoursTotal: number; courseTypes: string[];
}

// Arrivano interi e senza un ordine vero (quello in cui compaiono nei corsi):
// il predefinito diventa il nome, che è come si cerca qualcuno in un albo.
const ORDINI_DOCENTI: OpzioneOrdina<Teacher>[] = [
  { key: 'nome', label: 'Nome dalla A alla Z', criteri: [perTesto((t) => t.name)] },
  { key: 'attivi', label: 'Prima chi ha più corsi in corso', criteri: [perNumero((t) => t.activeCoursesCount, 'decrescente'), perTesto((t) => t.name)] },
  { key: 'ore', label: 'Prima chi ha fatto più ore', criteri: [perNumero((t) => Number(t.hoursTotal) || 0, 'decrescente'), perTesto((t) => t.name)] },
  // Gli interni non hanno una tariffa: finiscono in fondo in tutti e due i versi
  { key: 'tariffa', label: 'Tariffa oraria più bassa prima', criteri: [perNumero((t) => t.hourlyRate || null), perTesto((t) => t.name)] },
];

const FILTRI_DOCENTI: FiltroDef<Teacher>[] = [
  {
    param: 'tipo', label: 'Interni o esterni',
    opzioni: [
      { value: 'user', label: 'Solo i docenti interni' },
      { value: 'supplier', label: 'Solo i docenti esterni' },
    ],
    test: (t, v) => t.kind === v,
  },
  {
    param: 'solo', label: 'Corsi',
    opzioni: [{ value: 'attivi', label: 'Solo chi insegna in un corso in corso' }],
    test: (t) => t.activeCoursesCount > 0,
  },
];

function TeachersTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['talent-teachers'],
    queryFn: () => api.get<Teacher[]>('/talent/teachers'),
  });

  const items = data ?? [];
  const activeTeachers = items.filter((t) => t.activeCoursesCount > 0).length;
  const hoursTotal = items.reduce((a, t) => a + (Number(t.hoursTotal) || 0), 0);
  const ordina = useOrdina(ORDINI_DOCENTI);
  const filtri = useFiltri(FILTRI_DOCENTI, data);
  const righe = useMemo(() => ordina.applica(filtri.applica(data ?? [])), [data, ordina, filtri]);

  return (
    <>
      {/* .filterbar--float, come le altre 19 schermate (referto §2.10). */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <span className="mute text-[13px]">Docenti interni ed esterni assegnati ai corsi</span>
        <div className="spacer" style={{ flex: 1 }} />
        <span className="mono mute text-[12px]">{items.length} docenti · {activeTeachers} attivi · {hoursTotal}h erogate</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
      </div>
      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        {/* tbl--static: l'albo docenti non apre nessuna scheda, quindi la
            card non porta il chevron che vorrebbe dire "si apre". */}
        <table className="tbl tbl--ed tbl--card tbl--static">
          <thead><tr><th>Docente</th><th>Tipo</th><th>Tipologie corsi</th><th className="num">Corsi</th><th className="num">Ore</th><th>Email</th><th className="num">Tariffa</th></tr></thead>
          <tbody>
            {righe.map((t) => (
              <tr key={t.kind + ':' + t.id}>
                <td data-m="hd">
                  <div className="row">
                    <Avatar initials={t.initials} />
                    <span className="font-semibold">{t.name}</span>
                  </div>
                </td>
                {/* L'ordine delle celle è quello delle colonne del computer e
                    non si tocca: cambiarlo cambierebbe la tabella a 1440.
                    Tipologie ed email sono testi lunghi e prendono una riga a
                    testa; qui niente `off`, perché l'albo non apre nessuna
                    scheda e un dato nascosto sarebbe un dato perso. */}
                <td data-m="key" data-l="Tipo"><span className="chip-label">{t.kind === 'user' ? 'Interno' : 'Esterno'}</span></td>
                <td data-m="row" data-l="Tipologie corsi"><span className="chip-label">{t.courseTypes.join(' · ') || '—'}</span></td>
                <td data-m="key" data-l="Corsi" className="num mono">{t.coursesCount}<span className="mute text-[12px] ml-1">({t.activeCoursesCount} att.)</span></td>
                <td data-m="key" data-l="Ore" className="num mono">{t.hoursTotal}h</td>
                <td data-m="row" data-l="Email" className="mute text-[12px]">{t.email ?? '—'}</td>
                <td data-m="key" data-l="Tariffa" className="num mono">{t.hourlyRate ? `€ ${t.hourlyRate}/h` : '—'}</td>
              </tr>
            ))}
            {!isLoading && !righe.length && (
              <tr>
                <td colSpan={7} style={{ padding: 0 }}>
                  {items.length ? (
                    <EmptyState
                      icon="users"
                      title="Nessun docente con questi filtri"
                      description="Togli un filtro per vedere gli altri."
                      action={<button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
                    />
                  ) : (
                    <EmptyState
                      icon="users"
                      title="Nessun docente registrato"
                      description="I docenti compaiono qui quando vengono assegnati ai corsi."
                    />
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================================
// TALENT POOL (Academy) — anagrafica leggera dei candidati/discenti
// =====================================================================
interface CandidateItem {
  id: string; firstName: string; lastName: string; city: string | null; skill: string | null;
  seniority: string | null; stage: CandidateStage; availability: string | null;
  enrollments: Array<{ course: { name: string } }>;
}

interface PaginatedCandidates {
  items: CandidateItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Paginati: ordina l'API. `fase` è l'ordine di sempre.
const ORDINI_CANDIDATI: OpzioneOrdina[] = [
  { key: 'fase', label: 'Fase del recruiting, dai nuovi agli assunti' },
  { key: 'cognome', label: 'Cognome dalla A alla Z' },
  { key: 'recenti', label: 'Aggiunti più di recente prima' },
];

const FASI_CANDIDATO: Array<{ value: keyof typeof CandidateStage; label: string }> = [
  { value: 'NUOVO_CANDIDATO', label: 'Solo i nuovi candidati' },
  { value: 'SCREENING', label: 'Solo quelli in screening' },
  { value: 'IDONEO', label: 'Solo gli idonei' },
  { value: 'ISCRITTO_AL_CORSO', label: 'Solo gli iscritti a un corso' },
  { value: 'FREQUENTANTE', label: 'Solo chi sta frequentando' },
  { value: 'PRONTO_PER_PLACEMENT', label: 'Solo i pronti per il placement' },
  { value: 'PROPOSTO_AD_AZIENDA', label: "Solo i proposti a un'azienda" },
  { value: 'COLLOQUIO_FISSATO', label: 'Solo chi ha un colloquio fissato' },
  { value: 'SOMMINISTRATO', label: 'Solo i somministrati' },
  { value: 'ASSUNTO', label: 'Solo gli assunti' },
  { value: 'SCARTATO', label: 'Solo gli scartati' },
];
const FILTRI_CANDIDATI: FiltroDef[] = [
  { param: 'fase', label: 'Fase del recruiting', opzioni: FASI_CANDIDATO },
];

function CandidatesTable() {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const [page, setPage] = useState(1);
  const ordina = useOrdina(ORDINI_CANDIDATI);
  const filtri = useFiltri(FILTRI_CANDIDATI);
  const fase = filtri.valore('fase');
  useEffect(() => { setPage(1); }, [ordina.key, filtri.chiave]);
  const { data, isLoading } = useQuery({
    queryKey: ['candidates', 'list', page, ordina.key, fase],
    queryFn: () => api.get<PaginatedCandidates>(
      `/candidates?page=${page}&pageSize=${PAGE_SIZE}&sort=${ordina.key}${fase ? `&stage=${encodeURIComponent(fase)}` : ''}`,
    ),
  });
  const items = data?.items ?? [];
  return (
    <>
      {/* .filterbar--float, come le altre 19 schermate (referto §2.10). */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <span className="mute text-[13px]">Il registro da cui pescare per le pipeline di recruiting dei corsi</span>
        <div className="spacer" style={{ flex: 1 }} />
        <span className="mono mute text-[12px]">{data?.total ?? 0} candidati</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
        <button className="btn btn-sm btn-primary d-only" onClick={() => openModal('newCandidate')}><Icon name="plus" size={12} /> Nuovo candidato</button>
      </div>
      <DesktopOnly
        cosa="L'inserimento di un nuovo candidato"
        perche="la scheda completa comprende profilo, competenze, disponibilità e documenti."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newCandidate')}><Icon name="plus" size={12} /> Nuovo candidato</button>
        </div>
      </DesktopOnly>
      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
        <thead>
          <tr>
            <th>Candidato</th><th>Città</th><th>Competenza</th><th>Seniority</th><th>Corso</th><th>Stato</th><th>Disponibilità</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} onClick={() => navigate(`/candidates/${c.id}`)}>
              {/* Città e seniority si leggono aprendo la scheda del
                  candidato: qui restano le quattro che fanno decidere se
                  aprirla (mobile.md §3). L'ordine delle celle è quello delle
                  colonne del computer e non si tocca. */}
              <td data-m="hd">
                <div className="row">
                  <Avatar initials={`${c.firstName[0]}${c.lastName[0]}`} />
                  <span className="font-semibold">{c.firstName} {c.lastName}</span>
                </div>
              </td>
              <td data-m="off">{c.city}</td>
              <td data-m="key" data-l="Competenza">{c.skill}</td>
              <td data-m="off"><span className="chip-label">{c.seniority ?? '—'}</span></td>
              <td data-m="row" data-l="Corso" className="mute">{c.enrollments[0]?.course.name ?? '—'}</td>
              <td data-m="key" data-l="Stato"><Chip>{c.stage.replace(/_/g, ' ').toLowerCase()}</Chip></td>
              <td data-m="key" data-l="Disponibilità" className="mono mute">{c.availability}</td>
            </tr>
          ))}
          {!isLoading && !items.length && (
            <tr>
              <td colSpan={7} style={{ padding: 0 }}>
                {filtri.attivi > 0 ? (
                  <EmptyState
                    icon="users"
                    title="Nessun candidato in questa fase"
                    description="Togli il filtro per vedere gli altri."
                    action={<button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
                  />
                ) : (
                  <EmptyState
                    icon="users"
                    title="Nessun candidato in anagrafica"
                    description="Aggiungi il primo dal pulsante «Nuovo candidato»."
                  />
                )}
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>
      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
      )}
    </>
  );
}

// =====================================================================
// CORSI (Academy)
// =====================================================================
interface CourseItem {
  id: string; name: string; type: string; status: string; partnerName: string | null;
  client: { shortName: string } | null; startDate: string; endDate: string;
  hoursTotal: number; enrolledCount: number; revenue: string; cost: string;
}

interface PaginatedCourses {
  items: CourseItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Paginati: ordina l'API. `fase` è l'ordine di sempre (fase, poi i più recenti).
const ORDINI_CORSI: OpzioneOrdina[] = [
  { key: 'fase', label: 'Fase del corso, dalle iscrizioni alla chiusura' },
  { key: 'inizio', label: 'Iniziati più di recente prima' },
  { key: 'ricavo', label: 'Prima i corsi che rendono di più' },
  { key: 'iscritti', label: 'Prima i corsi con più iscritti' },
  { key: 'nome', label: 'Nome del corso dalla A alla Z' },
];

const FILTRI_CORSI: FiltroDef[] = [
  {
    param: 'tipo', label: 'Tipo di corso',
    opzioni: [
      { value: 'FORMATEMP', label: 'Solo i corsi Formatemp' },
      { value: 'FONDIMPRESA', label: 'Solo i corsi Fondimpresa' },
      { value: 'INTERNO', label: 'Solo i corsi interni' },
    ],
  },
  {
    param: 'stato', label: 'Fase del corso',
    opzioni: [
      { value: 'ISCRIZIONI', label: 'Solo quelli con le iscrizioni aperte' },
      { value: 'SELEZIONE', label: 'Solo quelli in selezione' },
      { value: 'AULA', label: 'Solo quelli in aula' },
      { value: 'VALUTAZIONE', label: 'Solo quelli in valutazione' },
      { value: 'PLACEMENT', label: 'Solo quelli in placement' },
      { value: 'RENDICONTAZIONE', label: 'Solo quelli da rendicontare' },
      { value: 'CHIUSO', label: 'Solo quelli chiusi' },
    ],
  },
];

function CoursesTable() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const showMargin = !!user?.role.canSeeMargin;
  const [page, setPage] = useState(1);
  const ordina = useOrdina(ORDINI_CORSI);
  const filtri = useFiltri(FILTRI_CORSI);
  const tipo = filtri.valore('tipo');
  const stato = filtri.valore('stato');
  useEffect(() => { setPage(1); }, [ordina.key, filtri.chiave]);
  const query = useMemo(() => {
    const sp = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), sort: ordina.key });
    if (tipo) sp.set('type', tipo);
    if (stato) sp.set('status', stato);
    return sp.toString();
  }, [page, ordina.key, tipo, stato]);
  const { data, isLoading } = useQuery({
    queryKey: ['courses', 'list', query],
    queryFn: () => api.get<PaginatedCourses>(`/courses?${query}`),
  });
  return (
    <>
      {/* .filterbar--float, come le altre 19 schermate (referto §2.10). */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <span className="mute text-[13px]">Percorsi formativi Formatemp e Fondimpresa</span>
        <div className="spacer" style={{ flex: 1 }} />
        <span className="mono mute text-[12px]">{data?.total ?? 0} corsi</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
        <button className="btn btn-sm btn-primary d-only" onClick={() => openModal('newCourse')}><Icon name="plus" size={12} /> Nuovo corso</button>
      </div>
      <DesktopOnly
        cosa="La creazione di un nuovo corso"
        perche="programma, partner, periodo, ore e iscritti sono una procedura da computer."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newCourse')}><Icon name="plus" size={12} /> Nuovo corso</button>
        </div>
      </DesktopOnly>
      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        <table className="tbl tbl--ed tbl--card">
        <thead>
          <tr>
            <th>Corso</th><th>Tipo</th><th>Partner / Azienda</th><th>Periodo</th><th>Ore</th><th>Iscritti</th><th>Stato</th>
            <th className="num">Ricavo</th>
            {showMargin && <th className="num">Margine</th>}
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).map((c) => {
            const rev = Number(c.revenue) || 0;
            const cost = Number(c.cost) || 0;
            const pct = rev > 0 ? ((rev - cost) / rev) * 100 : 0;
            return (
              <tr key={c.id} onClick={() => navigate(`/courses/${c.id}`)}>
                {/* Nove colonne facevano una tabella larga 862px dentro 358:
                    in scheda restano le quattro che fanno decidere se aprire
                    (stato, iscritti, ricavo, periodo), il resto si legge
                    aprendo il corso. L'ordine delle celle non si tocca. */}
                <td data-m="hd" className="font-semibold">{c.name}</td>
                <td data-m="off"><span className="chip-label">{c.type}</span></td>
                <td data-m="row" data-l="Partner / Azienda">{c.partnerName}{c.client && ` · ${c.client.shortName}`}</td>
                <td data-m="key" data-l="Periodo" className="mono mute text-[12px]">{fmtDate(c.startDate)} → {fmtDate(c.endDate)}</td>
                <td data-m="off" className="mono">{c.hoursTotal}h</td>
                <td data-m="key" data-l="Iscritti" className="mono">{c.enrolledCount}</td>
                <td data-m="key" data-l="Stato"><Chip tone={statusTone(c.status)}>{c.status.replace('_', ' ')}</Chip></td>
                <td data-m="key" data-l="Ricavo" className="num">€ {fmtEur(rev)}</td>
                {showMargin && <td data-m="off" className="num tbl-metric accent">{pct.toFixed(0)}%</td>}
              </tr>
            );
          })}
          {!isLoading && !data?.items.length && (
            <tr>
              <td colSpan={showMargin ? 9 : 8} style={{ padding: 0 }}>
                {filtri.attivi > 0 ? (
                  <EmptyState
                    icon="palette"
                    title="Nessun corso con questi filtri"
                    description="Togli un filtro per vedere gli altri."
                    action={<button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
                  />
                ) : (
                  <EmptyState
                    icon="palette"
                    title="Nessun corso registrato"
                    description="Crea il primo dal pulsante «Nuovo corso»."
                  />
                )}
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>
      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
      )}
    </>
  );
}
